/**
 * NotesPanel — Presentational panel for reservation notes (BookingDetailModal)
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 5b-1 extracted from BookingDetailModal.jsx; JSX moved verbatim. No logic changes.
 *
 * Purpose:
 *   Render the Notes expandable panel: loading/error/empty/list of notes with simple markdown.
 *   Presentational only; parent owns state, effects, and fetch. All data and formatDateTimeET passed as props.
 *
 * Assumptions & Invariants:
 *   - No fetch; no hooks; no new logic. Only props in, JSX out.
 *   - Parent owns notes state, notesLoading, notesError, and onClose (setTopPanelOpen(null)).
 *   - formatDateTimeET is passed from parent (dateTimeET) so panel stays dependency-free.
 *
 * Dependencies: React only
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 5b-1 extraction from BookingDetailModal (notes panel JSX only).
 */

export default function NotesPanel({
  theme,
  onClose,
  notesLoading,
  notesError,
  notes,
  formatDateTimeET,
}) {
  return (
    <div style={{
      borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(200, 195, 188, 0.4)'}`,
      padding: '16px 24px',
      backgroundColor: theme === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
      }}>
        <p style={{
          fontSize: '14px',
          fontWeight: 'var(--font-weight-medium)',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
        }}>
          Notes
        </p>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '12px',
            color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Close
        </button>
      </div>
      {notesLoading ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          Loading notes...
        </p>
      ) : notesError ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
          margin: 0,
        }}>
          Error: {notesError}
        </p>
      ) : notes.length === 0 ? (
        <p style={{
          fontSize: '14px',
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
          margin: 0,
          opacity: 0.7,
        }}>
          No notes are available for this reservation.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h4 style={{
            fontSize: '16px',
            fontWeight: 'var(--font-weight-semibold)',
            color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            margin: '0 0 8px 0',
          }}>
            Notes from CLC
          </h4>
          {notes.map((note) => (
            <div
              key={note.id}
              style={{
                padding: '12px',
                backgroundColor: theme === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
                borderRadius: '6px',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              }}
            >
              <div style={{
                fontSize: '12px',
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
                marginBottom: '8px',
                opacity: 0.7,
              }}>
                {formatDateTimeET(note.created_at, { dateOnly: false })}
                {note.created_by_name && ` • ${note.created_by_name}`}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.5',
              }}>
                {/* Simple Markdown rendering - basic support for bold, italic, lists */}
                {note.note_body.split('\n').map((line, idx) => {
                  // Simple markdown parsing
                  let content = line;
                  // Bold (**text**)
                  content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                  // Italic (*text*)
                  content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');
                  // Bullet list (- item)
                  if (line.trim().startsWith('- ')) {
                    return (
                      <div key={idx} style={{ marginLeft: '16px', marginBottom: '4px' }}>
                        • {content.replace(/^-\s*/, '')}
                      </div>
                    );
                  }
                  // Numbered list (1. item)
                  if (/^\d+\.\s/.test(line.trim())) {
                    return (
                      <div key={idx} style={{ marginLeft: '16px', marginBottom: '4px' }}>
                        {content}
                      </div>
                    );
                  }
                  // Quote (> text)
                  if (line.trim().startsWith('> ')) {
                    return (
                      <div key={idx} style={{
                        marginLeft: '16px',
                        paddingLeft: '12px',
                        borderLeft: `3px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'}`,
                        marginBottom: '4px',
                        fontStyle: 'italic',
                        opacity: 0.8,
                      }}
                      dangerouslySetInnerHTML={{ __html: content.replace(/^>\s*/, '') }}
                      />
                    );
                  }
                  return (
                    <div key={idx} style={{ marginBottom: '4px' }}
                      dangerouslySetInnerHTML={{ __html: content || '&nbsp;' }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
